
import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { GitBranch, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNodeSettings } from '../NodeSettingsContext';
import { BreakpointMarker } from '../BreakpointMarker';
import { type StateNodeData } from '@/types/workflow-graph';
import { cn } from '@/lib/utils';

interface ChoiceNodeProps {
    id: string;
    data: StateNodeData;
    selected?: boolean;
}

export const ChoiceNode = memo(function ChoiceNode({ id, data, selected }: ChoiceNodeProps) {
    const { openSettings } = useNodeSettings();
    const { 
        label, 
        layoutDirection = 'LR',
        isDebugMode,
        isCurrentNode,
        hasBreakpoint,
        onToggleBreakpoint,
    } = data;

    // Determine handle positions based on layout direction
    const isHorizontal = layoutDirection === 'LR';
    const inputPosition = isHorizontal ? Position.Left : Position.Top;
    const outputPosition = isHorizontal ? Position.Right : Position.Bottom;

    // Handle positioning styles based on layout direction
    const inputStyle = isHorizontal 
        ? { top: 20, left: -8, transform: 'translate(-50%, -50%)' }
        : { top: -8, left: 20, transform: 'translate(-50%, -50%)' };
    const outputStyle = isHorizontal 
        ? { top: 20, right: -8, transform: 'translate(50%, -50%)' }
        : { bottom: -8, left: 20, transform: 'translate(-50%, 50%)' };

    // Label position based on layout direction
    const labelPosition = isHorizontal 
        ? 'absolute left-1/2 top-full mt-2 -translate-x-1/2'
        : 'absolute top-1/2 left-full ml-2 -translate-y-1/2';

    return (
        <div className={cn('relative h-10 w-10', selected && 'drop-shadow-lg filter')} style={{ overflow: 'visible' }}>
            {/* Breakpoint Marker (debug mode only) */}
            {isDebugMode && onToggleBreakpoint && (
                <BreakpointMarker
                    nodeId={id}
                    isEnabled={hasBreakpoint ?? false}
                    isDebugMode={isDebugMode}
                    isCurrent={isCurrentNode ?? false}
                    onClick={onToggleBreakpoint}
                />
            )}

            {/* Settings button - positioned outside the rotated diamond */}
            <div className="absolute -right-4 -top-4 z-10">
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 rounded-full bg-white text-slate-400 shadow-sm border border-slate-200 hover:bg-slate-100 hover:text-slate-600"
                    title="Node Settings"
                    onClick={(e) => {
                        e.stopPropagation();
                        openSettings(data);
                    }}
                >
                    <Settings size={10} />
                </Button>
            </div>

            {/* Diamond shape using rotation */}
            <div
                className={cn(
                    'flex h-10 w-10 transform rotate-45 items-center justify-center rounded-sm border-2 border-blue-800 bg-blue-100',
                    selected && 'ring-2 ring-blue-500 ring-offset-2',
                    isCurrentNode && 'ring-4 ring-orange-400 ring-opacity-75 animate-pulse border-orange-500 shadow-[0_0_20px_5px_rgba(251,146,60,0.5)]'
                )}
            >
                <div className="-rotate-45 transform text-center">
                    <GitBranch className="h-4 w-4 text-blue-800" />
                </div>
            </div>

            {/* Label - position based on layout direction */}
            <div className={cn(labelPosition, 'text-center whitespace-nowrap')}>
                <span className="block text-xs font-medium text-slate-900">
                    {label}
                </span>
            </div>

            {/* Input Handle - position based on layout direction */}
            <Handle
                type="target"
                id="target"
                position={inputPosition}
                className="!h-2.5 !w-2.5 !border-blue-800 !bg-blue-700"
                style={inputStyle}
            />

            {/* Output Handle - position based on layout direction */}
            <Handle
                type="source"
                id="source"
                position={outputPosition}
                className="!h-2.5 !w-2.5 !border-blue-800 !bg-blue-700"
                style={outputStyle}
                isConnectable={true}
            />
        </div>
    );
});
